import * as t from "io-ts";
import { withFallback } from "io-ts-types/lib/withFallback";

import {
    ImageURL,
    ProductCategoryID,
    ProductID,
    SafeHTML,
    WagtailPageID,
    WebPageURL,
} from "./nominals";
import { Link as LinkSnippet } from "./streamfield-snippets";
import { NullNumber, nullable } from "./utils";

type LiteralValue = string | number | boolean;

export const StreamValue = <T extends LiteralValue, V extends t.Mixed>(
    type: T,
    value: V,
) => {
    return t.readonly(
        t.type({
            id: withFallback(t.string, ""),
            type: t.literal(type),
            value: value,
        }),
    );
};

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.BooleanBlock
 */
const BooleanBlock = t.boolean;
type BooleanBlock = t.TypeOf<typeof BooleanBlock>;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.CharBlock
 */
export const CharBlock = t.string;
export type CharBlock = t.TypeOf<typeof CharBlock>;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.ChoiceBlock
 */
const ChoiceBlock = t.string;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.ImageChooserBlock
 */
export const ImageChooserBlock = t.readonly(
    t.intersection([
        t.type({
            title: t.string,
            width: t.number,
            height: t.number,
            url: ImageURL,
        }),
        t.partial({
            renditions: t.record(
                t.string,
                t.union([
                    t.undefined,
                    t.readonly(
                        t.type({
                            spec: t.string,
                            width: t.number,
                            height: t.number,
                            url: ImageURL,
                        }),
                    ),
                ]),
            ),
        }),
    ]),
);
export type ImageChooserBlock = t.TypeOf<typeof ImageChooserBlock>;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.ListBlock
 */
export const ListBlock = t.readonlyArray;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.PageChooserBlock
 */
export const PageChooserBlock = t.readonly(
    t.type({
        id: WagtailPageID,
        url: WebPageURL,
    }),
);
export type PageChooserBlock = t.TypeOf<typeof PageChooserBlock>;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.RichTextBlock
 */
export const RichTextBlock = SafeHTML;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.SnippetChooserBlock
 */
export const SnippetChooserBlock = <T extends t.Mixed>(snip: T): T => snip;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.StructBlock
 */
export const StructBlock = <
    T extends Parameters<typeof t.type>[0],
    U extends Parameters<typeof t.partial>[0],
>(
    required: T,
    optional: U,
) => {
    return t.readonly(t.intersection([t.type(required), t.partial(optional)]));
};

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.abstract.StreamBlock
 */
const StreamBlock = <
    T extends Parameters<typeof t.union>[0],
    U extends Parameters<typeof t.union>[1],
>(
    codecs: T,
    name?: U,
) => {
    return t.readonlyArray(t.union(codecs, name));
};

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.background_styles.BackgroundStylesSubBlock
 */
export const BackgroundStylesSubBlock = StructBlock(
    {},
    {
        style: nullable(ChoiceBlock),
        background_image: nullable(ImageChooserBlock),
    },
);

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.responsive_visibility.ResponsiveVisibilitySubBlock
 */
export const ResponsiveVisibilitySubBlock = StructBlock(
    {},
    {
        visibility: nullable(ChoiceBlock),
    },
);

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.choosers.ProductAttributeChoiceBlock
 */
const ProductAttributeChoiceBlock = t.string;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.choosers.ProductOptionValueChoiceBlock
 */
export const ProductOptionValueChoiceBlock = t.string;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.choosers.ProductChooserSubBlock
 */
export const ProductChooserSubBlock = ProductID;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.choosers.CategoryChooserSubBlock
 */
const CategoryChooserSubBlock = t.readonly(
    t.type({
        id: ProductCategoryID,
        name: t.string,
    }),
);
type CategoryChooserSubBlock = t.TypeOf<typeof CategoryChooserSubBlock>;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.choosers.BundleGroupChooserSubBlock
 */
const BundleGroupChooserSubBlock = t.readonly(
    t.type({
        id: NullNumber,
        suggested_products_info: nullable(
            t.readonlyArray(
                t.readonly(
                    t.type({
                        product_id: ProductID,
                        shipping_class_name: t.string,
                    }),
                ),
            ),
        ),
    }),
);
type BundleGroupChooserSubBlock = t.TypeOf<typeof BundleGroupChooserSubBlock>;

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.spacing.SpacingSubBlock
 */

const SpacingClassType = t.union([
    t.literal(""),
    t.literal("spacing--xtra-small"),
    t.literal("spacing--small"),
    t.literal("spacing--medium"),
    t.literal("spacing--large"),
    t.literal("spacing--xtra-large"),
]);

export const SpacingSubBlock = StructBlock(
    {},
    {
        top_spacing: t.union([SpacingClassType, t.null]),
        top_inner_spacing: t.union([SpacingClassType, t.null]),
        bottom_spacing: t.union([SpacingClassType, t.null]),
        bottom_inner_spacing: t.union([SpacingClassType, t.null]),
    },
);

/**
 * See streamfield block: tsicommon.cms.blocks.sub_blocks.flair.FlairSubBlock
 */
export const FlairSubBlock = StructBlock(
    {},
    {
        style: nullable(ChoiceBlock),
        content: nullable(RichTextBlock),
    },
);

/**
 * See streamfield block: tsicommon.cms.models.variants.VariantDescriptionBlock
 */
export const VariantDescriptionBlock = StructBlock(
    {
        product: ProductChooserSubBlock,
    },
    {
        description: nullable(CharBlock),
        preorder_shipping_description: nullable(CharBlock),
    },
);
export type VariantDescriptionBlock = t.TypeOf<typeof VariantDescriptionBlock>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_cards._ComparisonCardProduct
 */
const ComparisonCardProduct = StructBlock(
    {
        show_flair: BooleanBlock,
        card_flair: FlairSubBlock,
        card_title: RichTextBlock,
        card_image: ImageChooserBlock,
        card_image_mobile: nullable(ImageChooserBlock),
        card_copy: RichTextBlock,
        card_product: ProductChooserSubBlock,
    },
    {},
);
export type IComparisonCardProduct = t.TypeOf<typeof ComparisonCardProduct>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_cards._ComparisonCardCategory
 */
const ComparisonCardCategory = StructBlock(
    {
        show_flair: BooleanBlock,
        card_flair: FlairSubBlock,
        card_title: RichTextBlock,
        card_image: ImageChooserBlock,
        card_image_mobile: nullable(ImageChooserBlock),
        card_copy: RichTextBlock,
        card_category: CategoryChooserSubBlock,
    },
    {},
);
export type IComparisonCardCategory = t.TypeOf<typeof ComparisonCardCategory>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_cards._ComparisonCardAttribute
 */
const ComparisonCardAttribute = StructBlock(
    {
        show_flair: BooleanBlock,
        card_flair: FlairSubBlock,
        card_title: RichTextBlock,
        card_image: ImageChooserBlock,
        card_image_mobile: nullable(ImageChooserBlock),
        card_copy: RichTextBlock,
        card_attribute: ProductAttributeChoiceBlock,
        card_attribute_value: ProductOptionValueChoiceBlock,
    },
    {},
);
export type IComparisonCardAttribute = t.TypeOf<typeof ComparisonCardAttribute>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_cards._ComparisonTabProduct
 */
const ComparisonTabProduct = StructBlock(
    {
        tab_name: RichTextBlock,
        tab_support_copy: CharBlock,
        tab_cards: ListBlock(ComparisonCardProduct),
    },
    {},
);
export type IComparisonTabProduct = t.TypeOf<typeof ComparisonTabProduct>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_cards._ComparisonTabCategory
 */
const ComparisonTabCategory = StructBlock(
    {
        tab_name: RichTextBlock,
        tab_support_copy: CharBlock,
        tab_cards: ListBlock(ComparisonCardCategory),
    },
    {},
);
export type IComparisonTabCategory = t.TypeOf<typeof ComparisonTabCategory>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_cards._ComparisonTabAttribute
 */
const ComparisonTabAttribute = StructBlock(
    {
        tab_name: RichTextBlock,
        tab_support_copy: CharBlock,
        tab_cards: ListBlock(ComparisonCardAttribute),
    },
    {},
);
export type IComparisonTabAttribute = t.TypeOf<typeof ComparisonTabAttribute>;

/**
 * See streamfield block: tsicommon.cms.blocks.tab_cards.TabCardsBlock
 */
export const TabCardsBlock = StructBlock(
    {
        hide_tabs: BooleanBlock,
        theme: t.partial({
            theme: t.string,
        }),
        spacing: SpacingSubBlock,
        header_content_desktop: RichTextBlock,
        header_content_mobile: RichTextBlock,
        aria_label: CharBlock,
        tabs: StreamBlock([
            StreamValue("tab_product", ComparisonTabProduct),
            StreamValue("tab_category", ComparisonTabCategory),
            StreamValue("tab_attribute", ComparisonTabAttribute),
        ]),
    },
    {},
);
export type ITabCardsBlock = t.TypeOf<typeof TabCardsBlock>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_upsell_modal._UpsellSlide
 */
const ProductUpsellModalBlockSlide = StructBlock(
    {
        image: ImageChooserBlock,
    },
    {
        slide_bundle: BundleGroupChooserSubBlock,
        slide_cta: CharBlock,
        slide_link: nullable(SnippetChooserBlock(LinkSnippet)),
        initial_mobile_slide: BooleanBlock,
    },
);
type ProductUpsellModalBlockSlide = t.TypeOf<
    typeof ProductUpsellModalBlockSlide
>;

/**
 * See streamfield block: tsicommon.cms.blocks.product_upsell_modal.ProductUpsellModalBlock
 */
export const ProductUpsellModalBlock = StructBlock(
    {},
    {
        header_title: CharBlock,
        header_copy: CharBlock,
        header_title_mobile: CharBlock,
        header_copy_mobile: CharBlock,
        slides: ListBlock(ProductUpsellModalBlockSlide),
        cta_copy: CharBlock,
    },
);
export type ProductUpsellModalBlock = t.TypeOf<typeof ProductUpsellModalBlock>;
